import { useInsforge } from '../provider/InsforgeProvider';
import type { ReactNode } from 'react';

export interface ConditionalProps {
  children: ReactNode;
}

/**
 * Conditional component that renders children only when user is signed out.
 *
 * Uses userId to determine rendering:
 * - undefined = not loaded yet (SSR or before hydration) -> render null
 * - null = loaded, user signed out -> render children
 * - string = loaded, user signed in -> render null
 *
 * This prevents hydration mismatches in Next.js SSR by ensuring
 * consistent rendering between server and client initial render.
 *
 * @component
 * @example
 * ```tsx
 * <SignedOut>
 *   <SignIn />
 * </SignedOut>
 * ```
 *
 * @param {ReactNode} children - React nodes to render when user is not authenticated
 * @returns {JSX.Element | null} Renders children when signed out, null otherwise
 */
export function SignedOut({ children }: ConditionalProps) {
  const { userId } = useInsforge();

  // undefined = not loaded yet, render null (prevents hydration mismatch)
  // null = loaded and signed out, render children
  // string = signed in, render null
  if (userId === null) {
    return <>{children}</>;
  }

  return null;
}
